/**
 * Lift2Fit — coaching workspace: Schedule & bookings (`#/crm/calendar`).
 *
 * A week (and a day) view of the coach's availability slots. Every rule about
 * capacity lives in the API: this screen renders `booked / capacity`, who holds
 * each place, and shows the API's own refusal text when a booking, a resize or a
 * cancellation is declined. It never optimistically books.
 *
 * Deliberately TeamUp-shaped and no more: booking and availability, not venue
 * management.
 */
import { useMemo, useState } from 'react';
import {
  api,
  type CoachPackOwner,
  type CoachRoster,
  type CrmRecurrenceInput,
  type CrmRecurrenceRule,
  type CrmSlot,
  type CrmSlotKind,
  type CrmSlotPatch,
  type CrmSlotStatus,
  type Problem,
} from '@/lib/api';
import { cn } from '@/lib/format';
import { Button } from '@/components/ui';
import {
  AlertIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  PlusIcon,
  SyncIcon,
  TimerIcon,
  TrashIcon,
} from '@/lib/icons';
import {
  EmptyState,
  Field,
  LoadingPanel,
  Panel,
  ScreenHeader,
  StatusChip,
  type ChipTone,
} from '../components';
import { invalidate } from '@/lib/readCache';
import { calendarKey } from '@/lib/calendarKeys';
import { useAsync, useCachedAsync } from '../hooks';
import {
  CRM_EYEBROW,
  CrmMessage,
  RefusalNotice,
  addDays,
  bookingStatusMeta,
  formatDayHeading,
  formatSlotRange,
  fromDateTimeInput,
  isSameLocalDay,
  slotKindLabel,
  startOfIsoWeek,
  startOfLocalDay,
  toDateInput,
  toDateTimeInput,
} from './shared';

interface Notice {
  text: string;
  tone: ChipTone;
}

/* ---------------------------------------------------------------------------
   Recurrence — the draft the coach edits, and the API input it becomes.
   The API caps a series at 12 months; the copy says so before the request.
   ------------------------------------------------------------------------ */

type RepeatRuleChoice = 'none' | CrmRecurrenceRule;
type RepeatEndMode = 'count' | 'until' | 'none';

interface RepeatDraft {
  rule: RepeatRuleChoice;
  endMode: RepeatEndMode;
  occurrences: string;
  until: string;
}

const RECURRENCE_HORIZON_MONTHS = 12;

const NO_REPEAT: RepeatDraft = { rule: 'none', endMode: 'count', occurrences: '8', until: '' };

/** `null` means "create one slot, no series". */
function repeatInput(draft: RepeatDraft): CrmRecurrenceInput | null {
  if (draft.rule === 'none') return null;
  if (draft.endMode === 'count') {
    return { rule: draft.rule, occurrences: Math.max(2, Math.round(Number(draft.occurrences) || 2)) };
  }
  if (draft.endMode === 'until' && draft.until !== '') {
    return { rule: draft.rule, until: draft.until };
  }
  return { rule: draft.rule };
}

function ruleLabel(rule: RepeatRuleChoice): string {
  return rule === 'none' ? 'None' : `${rule.charAt(0).toUpperCase()}${rule.slice(1)}`;
}

/** A plain-English summary of the draft, shown beside the controls. */
function repeatSummary(draft: RepeatDraft): string {
  if (draft.rule === 'none') return 'No repeat — one slot.';
  if (draft.endMode === 'count') return `Repeats ${draft.rule}, ${draft.occurrences} occurrences.`;
  if (draft.endMode === 'until') return `Repeats ${draft.rule} until ${draft.until || 'the date you pick'}.`;
  return `Repeats ${draft.rule} for up to ${RECURRENCE_HORIZON_MONTHS} months.`;
}

/** The shared Repeat / Ends control, used by the new-slot form and the week action. */
function RepeatControls({
  draft,
  onChange,
  prefix,
}: {
  draft: RepeatDraft;
  onChange: (next: RepeatDraft) => void;
  prefix: string;
}) {
  return (
    <fieldset className="flex flex-col gap-3 border border-hairline px-3 py-3">
      <legend className="px-1 text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
        Repeat
      </legend>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Repeat rule">
        {(['none', 'weekly', 'monthly', 'yearly'] as const).map((rule) => (
          <Button
            key={rule}
            size="sm"
            variant={draft.rule === rule ? 'secondary' : 'outline'}
            aria-pressed={draft.rule === rule}
            data-testid={`${prefix}-rule-${rule}`}
            onClick={() => onChange({ ...draft, rule })}
          >
            {ruleLabel(rule)}
          </Button>
        ))}
      </div>

      {draft.rule !== 'none' ? (
        <>
          <div className="flex flex-wrap gap-1" role="group" aria-label="Repeat end condition">
            {(
              [
                ['count', 'After N times'],
                ['until', 'Until a date'],
                ['none', 'No end'],
              ] as const
            ).map(([mode, label]) => (
              <Button
                key={mode}
                size="sm"
                variant={draft.endMode === mode ? 'secondary' : 'outline'}
                aria-pressed={draft.endMode === mode}
                data-testid={`${prefix}-end-${mode}`}
                onClick={() => onChange({ ...draft, endMode: mode })}
              >
                {label}
              </Button>
            ))}
          </div>

          {draft.endMode === 'count' ? (
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                Occurrences (including the first)
              </span>
              <input
                type="number"
                min={2}
                max={500}
                data-testid={`${prefix}-occurrences`}
                value={draft.occurrences}
                onChange={(event) => onChange({ ...draft, occurrences: event.target.value })}
                className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
              />
            </label>
          ) : null}

          {draft.endMode === 'until' ? (
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                Until (inclusive)
              </span>
              <input
                type="date"
                data-testid={`${prefix}-until`}
                value={draft.until}
                onChange={(event) => onChange({ ...draft, until: event.target.value })}
                className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
              />
            </label>
          ) : null}

          <p className="text-[12px] text-ink-muted" data-testid={`${prefix}-summary`}>
            {repeatSummary(draft)} A series is capped at {RECURRENCE_HORIZON_MONTHS} months from its
            first slot, and every occurrence obeys the slot's capacity.
          </p>
        </>
      ) : null}
    </fieldset>
  );
}

/** The next sensible start for a new slot on `day` (top of the next hour). */
function defaultSlotTimes(day: Date): { start: string; end: string } {
  const now = new Date();
  const base = isSameLocalDay(day, now) ? new Date(now) : startOfLocalDay(day);
  const start = new Date(base);
  start.setMinutes(0, 0, 0);
  start.setSeconds(0, 0);
  if (isSameLocalDay(day, now)) start.setHours(start.getHours() + 1);
  else start.setHours(9, 0, 0, 0);
  return { start: toDateTimeInput(start), end: toDateTimeInput(new Date(start.getTime() + 3_600_000)) };
}

interface SlotEdit {
  slotId: string;
  start: string;
  end: string;
  kind: CrmSlotKind;
  capacity: string;
  location: string;
  notes: string;
  packId: string;
}

function editFromSlot(slot: CrmSlot): SlotEdit {
  return {
    slotId: slot.slotId,
    start: toDateTimeInput(new Date(slot.startsAt)),
    end: toDateTimeInput(new Date(slot.endsAt)),
    kind: slot.kind,
    capacity: String(slot.capacity),
    location: slot.location ?? '',
    notes: slot.notes ?? '',
    packId: slot.packId ?? '',
  };
}

export function CrmCalendarScreen() {
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [view, setView] = useState<'week' | 'day'>('week');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(() => ({
    ...defaultSlotTimes(new Date()),
    kind: 'individual' as CrmSlotKind,
    capacity: '1',
    location: '',
    notes: '',
    packId: '',
  }));
  const [editing, setEditing] = useState<SlotEdit | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [repeat, setRepeat] = useState<RepeatDraft>(NO_REPEAT);
  const [weekRepeatOpen, setWeekRepeatOpen] = useState(false);
  /**
   * "Which pack's slots am I looking at?" — a day/week of a busy gym mixes open
   * availability with three or four packs, and a coach checking one pack's
   * uptake had to read every chip's label to find them. `'all'` is the whole
   * week, `'open'` is the slots no pack owns, anything else is a pack id.
   */
  const [packFilter, setPackFilter] = useState<'all' | 'open' | string>('all');
  const [weekRepeat, setWeekRepeat] = useState<RepeatDraft>({ ...NO_REPEAT, rule: 'weekly' });
  const [expandedSlotId, setExpandedSlotId] = useState<string | null>(null);

  /** Open the new-slot form on `day`, resetting a repeat left from last time. */
  const openNewSlot = (day: Date) => {
    setForm({ ...defaultSlotTimes(day), kind: 'individual', capacity: '1', location: '', notes: '', packId: '' });
    setRepeat(NO_REPEAT);
    setFormOpen(true);
  };

  const weekStart = useMemo(() => startOfIsoWeek(anchor), [anchor]);
  const from = weekStart.toISOString();
  const to = addDays(weekStart, 7).toISOString();
  const slotsKey = calendarKey({ from, to });

  const slotsState = useCachedAsync(slotsKey, () => api.crmSlots(from, to), [from, to]);
  const rosterState = useCachedAsync('coaching/clients', () => api.coachClients(), []);
  const packsState = useCachedAsync('coaching/packs', () => api.coachPacks(), []);
  const slots = slotsState.data?.data ?? [];
  const roster: CoachRoster | null = rosterState.data;
  const clients = roster?.data ?? [];
  const livePacks: CoachPackOwner[] = (packsState.data ?? []).filter((pack) => pack.archivedAt === null);
  const reload = () => {
    // instant paint: a booking, a slot edit or an assignment changes what other
    // screens are showing, so drop the affected reads before refetching.
    invalidate('crm');
    invalidate('coaching');
    slotsState.reload();
    rosterState.reload();
  };

  const issue = (problem: Problem | undefined, fallbackText: string) => {
    setNotice(null);
    setRefusal(problem?.detail ?? problem?.title ?? fallbackText);
  };

  const succeed = (text: string) => {
    setRefusal(null);
    setNotice({ text, tone: 'success' });
    reload();
  };

  /* ------------------------------- mutations ----------------------------- */

  const create = async () => {
    setRefusal(null);
    setNotice(null);
    const startsAt = fromDateTimeInput(form.start);
    const endsAt = fromDateTimeInput(form.end);
    if (!startsAt || !endsAt) {
      issue(undefined, 'Enter both a start and an end time.');
      return;
    }
    if (Date.parse(endsAt) <= Date.parse(startsAt)) {
      issue(undefined, 'The end time must be after the start time.');
      return;
    }
    const capacity = form.kind === 'individual' ? 1 : Math.max(1, Math.round(Number(form.capacity) || 1));
    const slotInput = {
      startsAt,
      endsAt,
      kind: form.kind,
      capacity,
      location: form.location.trim() === '' ? null : form.location.trim(),
      notes: form.notes.trim() === '' ? null : form.notes.trim(),
      packId: form.packId === '' ? null : form.packId,
    };
    const recurrence = repeatInput(repeat);
    setBusy('create');
    if (recurrence) {
      const result = await api.crmCreateSlotBatch({ slots: [slotInput], recurrence });
      setBusy(null);
      if (result.problem || !result.data) {
        issue(result.problem, 'The repeating slots were not created.');
        return;
      }
      setFormOpen(false);
      setRepeat(NO_REPEAT);
      succeed(
        `${result.data.created} ${slotKindLabel(form.kind)} slot(s) created — a ${recurrence.rule} series. ` +
          'Every occurrence obeys the slot capacity.',
      );
      return;
    }
    const result = await api.crmCreateSlot(slotInput);
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'The slot was not created.');
      return;
    }
    setFormOpen(false);
    succeed(`${slotKindLabel(result.data.kind)} slot created with ${result.data.capacity} place(s).`);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const startsAt = fromDateTimeInput(editing.start);
    const endsAt = fromDateTimeInput(editing.end);
    if (!startsAt || !endsAt) {
      issue(undefined, 'Enter both a start and an end time.');
      return;
    }
    if (Date.parse(endsAt) <= Date.parse(startsAt)) {
      issue(undefined, 'The end time must be after the start time.');
      return;
    }
    const patch: CrmSlotPatch = {
      startsAt,
      endsAt,
      kind: editing.kind,
      capacity: editing.kind === 'individual' ? 1 : Math.max(1, Math.round(Number(editing.capacity) || 1)),
      location: editing.location.trim() === '' ? null : editing.location.trim(),
      notes: editing.notes.trim() === '' ? null : editing.notes.trim(),
      packId: editing.packId === '' ? null : editing.packId,
    };
    setBusy(editing.slotId);
    const result = await api.crmUpdateSlot(editing.slotId, patch);
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That change was refused.');
      return;
    }
    setEditing(null);
    succeed('Slot updated.');
  };

  const setSlotStatus = async (slot: CrmSlot, status: CrmSlotStatus) => {
    setBusy(slot.slotId);
    const result = await api.crmUpdateSlot(slot.slotId, { status });
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That change was refused.');
      return;
    }
    succeed(status === 'cancelled' ? 'Slot cancelled — no new bookings are accepted.' : 'Slot reopened.');
  };

  const removeSlot = async (slot: CrmSlot) => {
    setBusy(slot.slotId);
    const result = await api.crmDeleteSlot(slot.slotId);
    setBusy(null);
    setConfirmDelete(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That slot was not deleted.');
      return;
    }
    succeed('Slot deleted.');
  };

  const book = async (slot: CrmSlot, clientId: string) => {
    if (!clientId) {
      issue(undefined, 'Pick a client to book first.');
      return;
    }
    setBusy(slot.slotId);
    const result = await api.crmBookSlot(slot.slotId, clientId);
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That booking was refused.');
      return;
    }
    succeed(`${result.data.booking.clientName} booked into ${formatSlotRange(slot.startsAt, slot.endsAt)}.`);
  };

  const cancelPlace = async (slot: CrmSlot, bookingId: string) => {
    setBusy(slot.slotId);
    const result = await api.crmCancelBooking(bookingId);
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That booking was not cancelled.');
      return;
    }
    succeed('Booking cancelled — the place is free again.');
  };

  /**
   * "Repeat this week": one request attaches the week's one-off slots to a new
   * series and the server expands the future occurrences in one transaction.
   * The slots already on the calendar are left exactly where they are.
   *
   * It repeats what the screen is showing, so a coach who filtered to one pack
   * repeats THAT pack's slots for the next weeks — "this pack, every Tuesday, for
   * the next three months" — without touching the rest of their availability. The
   * button's count is the same list, so what it promises is what it sends.
   */
  const repeatThisWeek = async () => {
    setRefusal(null);
    setNotice(null);
    const candidates = repeatableSlots;
    if (candidates.length === 0) {
      issue(
        undefined,
        packFilter === 'all'
          ? 'There are no one-off open slots in this week to repeat.'
          : 'There are no one-off open slots for this pack in this week to repeat.',
      );
      return;
    }
    const recurrence = repeatInput(weekRepeat);
    if (!recurrence) {
      issue(undefined, 'Pick a repeat rule first.');
      return;
    }
    setBusy('repeat-week');
    const result = await api.crmRepeatSlots(
      candidates.map((slot) => slot.slotId),
      recurrence,
    );
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That week was not repeated.');
      return;
    }
    setWeekRepeatOpen(false);
    succeed(
      `${result.data.created} future slot(s) created from ${candidates.length} this week — one ${recurrence.rule} series.`,
    );
  };

  /**
   * Stop a series. The API cancels future unbooked occurrences, leaves the past
   * alone and KEEPS a booked future occurrence open; it reports the kept count
   * so the coach knows a client still holds a place.
   */
  const stopSeries = async (slot: CrmSlot) => {
    if (!slot.seriesId) return;
    setBusy(slot.slotId);
    const result = await api.crmStopSlotSeries(slot.seriesId);
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That series was not stopped.');
      return;
    }
    const kept = result.data.keptBooked;
    succeed(
      `Series stopped — ${result.data.cancelled} future slot(s) cancelled.` +
        (kept > 0
          ? ` ${kept} future slot(s) stay open because a client holds a place; cancel those bookings individually if the session is off.`
          : ''),
    );
  };

  const markOutcome = async (slot: CrmSlot, bookingId: string, outcome: 'attend' | 'no-show') => {
    setBusy(slot.slotId);
    const result = await api.crmMarkBooking(bookingId, outcome);
    setBusy(null);
    if (result.problem || !result.data) {
      issue(result.problem, 'That outcome was not saved.');
      return;
    }
    succeed(outcome === 'attend' ? 'Marked attended.' : 'Marked no-show.');
  };

  /* -------------------------------- render ------------------------------- */

  /**
   * The slots the screen shows. The filter is client-side on purpose: the week
   * is one request, so filtering it costs nothing and keeps the pack chips
   * instant — and a slot's pack is already on the wire (`packId`/`packName`).
   */
  const visibleSlots = useMemo(() => {
    if (packFilter === 'all') return slots;
    if (packFilter === 'open') return slots.filter((slot) => slot.packId === null);
    return slots.filter((slot) => slot.packId === packFilter);
  }, [slots, packFilter]);

  const filterName =
    packFilter === 'all' || packFilter === 'open'
      ? null
      : (livePacks.find((pack) => pack.packId === packFilter)?.name ??
        slots.find((slot) => slot.packId === packFilter)?.packName ??
        null);

  const slotsByDay = useMemo(() => {
    const map = new Map<string, CrmSlot[]>();
    for (const slot of visibleSlots) {
      const key = toDateInput(new Date(slot.startsAt));
      const list = map.get(key) ?? [];
      list.push(slot);
      map.set(key, list);
    }
    return map;
  }, [visibleSlots]);

  /** This week's one-off open slots — what "Repeat this week" will act on. */
  const repeatableSlots = useMemo(
    () => visibleSlots.filter((slot) => slot.seriesId === null && slot.status === 'open'),
    [visibleSlots],
  );

  /**
   * The week view's compact chip: the time and the exact `booked/capacity`, and
   * nothing else. Colour is a small status signal on the left edge and the
   * count — never a repaint of the cell. Clicking expands that slot into the
   * detailed day view.
   */
  const renderChip = (slot: CrmSlot) => {
    const full = slot.status !== 'cancelled' && slot.booked >= slot.capacity;
    const partial = slot.status !== 'cancelled' && !full && slot.booked > 0;
    const state = slot.status === 'cancelled' ? 'cancelled' : full ? 'full' : partial ? 'partial' : 'open';
    return (
      <li key={slot.slotId} data-testid={`crm-chip-row-${slot.slotId}`}>
        <button
          type="button"
          data-testid={`crm-chip-${slot.slotId}`}
          data-status={state}
          aria-label={`${formatSlotRange(slot.startsAt, slot.endsAt)} · ${slot.booked} of ${slot.capacity} places taken${
            full ? ' · Full' : ''
          }${slot.status === 'cancelled' ? ' · Cancelled' : ''}${
            slot.packName ? ` · ${slot.packName} pack` : ''
          }`}
          onClick={() => {
            setExpandedSlotId(slot.slotId);
            setView('day');
            setAnchor(startOfLocalDay(new Date(slot.startsAt)));
            setEditing(null);
            setConfirmDelete(null);
          }}
          className={cn(
            'flex w-full min-w-0 flex-col items-start gap-0.5 rounded-chat border border-hairline border-l-2 px-2 py-1.5 text-left shadow-[0_1px_2px_rgba(17,17,17,0.05)] transition duration-150 hover:-translate-y-px hover:border-accent hover:shadow-float',
            state === 'full'
              ? 'bg-surface bg-[linear-gradient(135deg,rgba(255,87,34,0.14),rgba(255,87,34,0.03)_55%,transparent)]'
              : 'bg-surface',
            state === 'full'
              ? 'border-l-danger bg-accent/10 shadow-[0_1px_3px_rgba(255,87,34,0.18)]'
              : state === 'partial'
                ? 'border-l-accent'
                : 'border-l-hairline',
            state === 'cancelled' && 'opacity-70',
          )}
        >
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span className="l2f-tabular truncate font-mono text-[11px] font-bold text-ink">
              {formatSlotRange(slot.startsAt, slot.endsAt)}
            </span>
            <span
              data-testid={`crm-chip-count-${slot.slotId}`}
              className={cn(
                'l2f-tabular shrink-0 font-mono text-[12px] font-bold',
                state === 'full'
                  ? 'text-danger'
                  : state === 'partial'
                    ? 'text-accent'
                    : 'text-ink-muted',
              )}
            >
              {slot.booked}/{slot.capacity}
            </span>
          </span>
          {full ? (
            <span
              data-testid={`crm-chip-status-${slot.slotId}`}
              className="text-[10px] font-bold uppercase tracking-[0.08em] text-danger"
            >
              Full
            </span>
          ) : null}
          {slot.status === 'cancelled' ? (
            <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-ink-muted">Cancelled</span>
          ) : null}
          {slot.recurrenceRule ? (
            <span
              data-testid={`crm-chip-series-${slot.slotId}`}
              className="flex items-center gap-1 text-[10px] uppercase tracking-[0.06em] text-ink-muted"
            >
              <SyncIcon className="text-[11px]" />
              {slot.recurrenceRule}
              {slot.seriesStopped ? ' · stopped' : ''}
            </span>
          ) : null}
          {slot.packName ? (
            <span
              data-testid={`crm-slot-pack-${slot.slotId}`}
              className="w-full truncate text-[10px] font-bold uppercase tracking-[0.06em] text-info"
            >
              {slot.packName}
            </span>
          ) : null}
        </button>
      </li>
    );
  };

  const renderSlot = (slot: CrmSlot) => {
    const remaining = Math.max(0, slot.capacity - slot.booked);
    const cancelled = slot.status === 'cancelled';
    const choice = choices[slot.slotId] ?? clients[0]?.userId ?? '';
    return (
      <li
        key={slot.slotId}
        data-testid={`crm-slot-${slot.slotId}`}
        data-expanded={expandedSlotId === slot.slotId ? 'true' : undefined}
        className={cn(
          'flex flex-col gap-2 border border-hairline bg-surface p-3',
          expandedSlotId === slot.slotId && 'border-accent',
          cancelled && 'opacity-70',
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <span className="flex min-w-0 flex-col gap-1">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
                {formatSlotRange(slot.startsAt, slot.endsAt)}
              </span>
              <StatusChip tone={slot.kind === 'group' ? 'accent' : 'neutral'}>{slotKindLabel(slot.kind)}</StatusChip>
              {cancelled ? <StatusChip tone="neutral" icon={<AlertIcon />}>Cancelled</StatusChip> : null}
              {slot.recurrenceRule ? (
                <StatusChip tone="neutral" icon={<SyncIcon />}>
                  {`Repeats ${slot.recurrenceRule}${slot.seriesStopped ? ' · stopped' : ''}`}
                </StatusChip>
              ) : null}
              {slot.packName ? (
                <span data-testid={`crm-slot-pack-${slot.slotId}`}>
                  <StatusChip tone="info">{slot.packName} pack</StatusChip>
                </span>
              ) : null}
            </span>
            {slot.location ? <span className="text-[12px] text-ink-muted">{slot.location}</span> : null}
          </span>
          <span className="flex flex-wrap items-center gap-1">
            {slot.seriesId && !slot.seriesStopped ? (
              <Button
                size="sm"
                variant="ghost"
                data-testid={`crm-stop-series-${slot.slotId}`}
                disabled={busy === slot.slotId}
                onClick={() => void stopSeries(slot)}
              >
                Stop series
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              data-testid={`crm-slot-edit-${slot.slotId}`}
              onClick={() => {
                setEditing(editFromSlot(slot));
                setConfirmDelete(null);
              }}
            >
              Edit
            </Button>
            {cancelled ? (
              <Button
                size="sm"
                variant="ghost"
                data-testid={`crm-slot-reopen-${slot.slotId}`}
                onClick={() => void setSlotStatus(slot, 'open')}
              >
                Reopen
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                data-testid={`crm-slot-cancel-${slot.slotId}`}
                onClick={() => void setSlotStatus(slot, 'cancelled')}
              >
                Cancel slot
              </Button>
            )}
            {slot.booked === 0 ? (
              confirmDelete === slot.slotId ? (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`crm-slot-delete-confirm-${slot.slotId}`}
                    onClick={() => void removeSlot(slot)}
                  >
                    Confirm delete
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>
                    Keep
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`crm-slot-delete-${slot.slotId}`}
                  onClick={() => setConfirmDelete(slot.slotId)}
                >
                  <TrashIcon />
                  Delete
                </Button>
              )
            ) : null}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span data-testid={`crm-slot-count-${slot.slotId}`} className="l2f-tabular font-mono text-[14px] font-bold text-ink">
            {slot.booked} / {slot.capacity}
          </span>
          <span className="text-[12px] text-ink-muted">
            {slot.booked === 1 ? 'place taken' : 'places taken'}
          </span>
          {slot.kind === 'group' ? (
            <span
              data-testid={`crm-slot-remaining-${slot.slotId}`}
              className={cn(
                'text-[12px] font-bold uppercase tracking-[0.06em]',
                remaining === 0 ? 'text-danger' : 'text-accent',
              )}
            >
              {remaining === 0
                ? `Full — all ${slot.capacity} places taken`
                : `${remaining} of ${slot.capacity} places left`}
            </span>
          ) : null}
        </div>

        {slot.notes ? <p className="text-[12px] text-ink-muted">{slot.notes}</p> : null}

        {slot.places.length ? (
          <ul className="flex flex-col">
            {slot.places.map((place) => {
              const meta = bookingStatusMeta(place.status);
              return (
                <li
                  key={place.bookingId}
                  data-testid={`crm-place-${place.bookingId}`}
                  className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline py-1.5"
                >
                  <span className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className={cn('truncate text-[13px] font-bold', place.linked ? 'text-ink' : 'text-ink-muted')}>
                      {place.clientName}
                    </span>
                    <StatusChip tone={meta.tone}>{meta.label}</StatusChip>
                    {!place.linked ? <span className="text-[11px] text-ink-muted">link revoked</span> : null}
                  </span>
                  {place.status === 'booked' ? (
                    <span className="flex flex-wrap items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`crm-place-attend-${place.bookingId}`}
                        disabled={busy === slot.slotId}
                        onClick={() => void markOutcome(slot, place.bookingId, 'attend')}
                      >
                        <CheckIcon />
                        Attended
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`crm-place-noshow-${place.bookingId}`}
                        disabled={busy === slot.slotId}
                        onClick={() => void markOutcome(slot, place.bookingId, 'no-show')}
                      >
                        No-show
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`crm-place-cancel-${place.bookingId}`}
                        disabled={busy === slot.slotId}
                        onClick={() => void cancelPlace(slot, place.bookingId)}
                      >
                        Cancel
                      </Button>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-[12px] text-ink-muted">No places booked yet.</p>
        )}

        {!cancelled ? (
          <div className="flex flex-col gap-2 border-t border-hairline pt-2">
            {clients.length ? (
              <>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="flex min-w-[160px] flex-1 flex-col gap-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Book a client</span>
                    <select
                      data-testid={`crm-book-select-${slot.slotId}`}
                      value={choice}
                      onChange={(event) =>
                        setChoices((previous) => ({ ...previous, [slot.slotId]: event.target.value }))
                      }
                      className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
                    >
                      <option value="">Choose a client…</option>
                      {clients.map((client) => (
                        <option key={client.userId} value={client.userId}>
                          {client.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`crm-book-submit-${slot.slotId}`}
                    disabled={busy === slot.slotId}
                    onClick={() => void book(slot, choice)}
                  >
                    Book place
                  </Button>
                </div>
                {remaining === 0 ? (
                  // Capacity is the API's rule, not this screen's: the control
                  // stays available and the API's refusal is shown word for word.
                  <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-danger">
                    This slot is full — a new booking will be refused by the API. Cancel a booking or raise the
                    capacity to open a place.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="text-[12px] text-ink-muted">Invite a client before you can book one.</p>
            )}
          </div>
        ) : null}

        {editing?.slotId === slot.slotId ? (
          <div
            data-testid={`crm-edit-form-${slot.slotId}`}
            className="flex flex-col gap-3 border border-accent bg-page-subtle p-3"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Starts">
                <input
                  type="datetime-local"
                  data-testid={`crm-edit-start-${slot.slotId}`}
                  value={editing.start}
                  onChange={(event) => setEditing({ ...editing, start: event.target.value })}
                  className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
                />
              </Field>
              <Field label="Ends">
                <input
                  type="datetime-local"
                  data-testid={`crm-edit-end-${slot.slotId}`}
                  value={editing.end}
                  onChange={(event) => setEditing({ ...editing, end: event.target.value })}
                  className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
                />
              </Field>
              <Field label="Kind">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={editing.kind === 'individual' ? 'secondary' : 'outline'}
                    data-testid={`crm-edit-kind-individual-${slot.slotId}`}
                    onClick={() => setEditing({ ...editing, kind: 'individual', capacity: '1' })}
                  >
                    1-to-1
                  </Button>
                  <Button
                    size="sm"
                    variant={editing.kind === 'group' ? 'secondary' : 'outline'}
                    data-testid={`crm-edit-kind-group-${slot.slotId}`}
                    onClick={() =>
                      setEditing({ ...editing, kind: 'group', capacity: editing.capacity === '1' ? '3' : editing.capacity })
                    }
                  >
                    Group
                  </Button>
                </div>
              </Field>
              <Field label="Capacity">
                <input
                  type="number"
                  min={1}
                  max={500}
                  data-testid={`crm-edit-capacity-${slot.slotId}`}
                  value={editing.capacity}
                  disabled={editing.kind === 'individual'}
                  onChange={(event) => setEditing({ ...editing, capacity: event.target.value })}
                  className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink disabled:opacity-50"
                />
              </Field>
              <Field label="Pack" info="Which pack this slot serves. Open is bookable by any of your clients.">
                <select
                  data-testid={`crm-edit-pack-${slot.slotId}`}
                  value={editing.packId}
                  onChange={(event) => setEditing({ ...editing, packId: event.target.value })}
                  className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
                >
                  <option value="">Open — any client</option>
                  {livePacks.map((pack) => (
                    <option key={pack.packId} value={pack.packId}>
                      {pack.name}
                    </option>
                  ))}
                  {slot.packId && !livePacks.some((pack) => pack.packId === slot.packId) ? (
                    <option value={slot.packId}>{slot.packName ?? 'Archived pack'}</option>
                  ) : null}
                </select>
              </Field>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" data-testid={`crm-edit-save-${slot.slotId}`} disabled={busy === slot.slotId} onClick={() => void saveEdit()}>
                Save slot
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
            <p className="text-[12px] text-ink-muted">
              Lowering capacity below the places already taken is refused by the API — cancel a booking first.
            </p>
          </div>
        ) : null}
      </li>
    );
  };

  if (slotsState.loading && !slotsState.data) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Loading your week" rows={4} />
        <LoadingPanel label="Loading your clients" rows={3} />
      </div>
    );
  }

  if (!slotsState.data) {
    return (
      <EmptyState
        title="We could not read your schedule"
        body={slotsState.error ?? 'The API did not answer. Check that you are signed in as a coach.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <Button variant="outline" onClick={reload}>
            Try again
          </Button>
        }
      />
    );
  }

  const days = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  const daySlots = slotsByDay.get(toDateInput(anchor)) ?? [];
  const rangeLabel = `${formatDayHeading(weekStart)} – ${formatDayHeading(addDays(weekStart, 6))}`;

  return (
    <div className="flex flex-col gap-6" data-testid="crm-calendar">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title="Schedule"
        badge={<StatusChip tone="accent">Coaching workspace</StatusChip>}
        lead="Your availability and who holds each place. The API enforces capacity — a full slot stays full."
        actions={
          formOpen ? undefined : (
            <span className="flex flex-wrap items-center gap-2">
              {view === 'week' && repeatableSlots.length > 0 ? (
                <Button
                  variant="outline"
                  data-testid="crm-repeat-week"
                  onClick={() => {
                    setWeekRepeat({ ...NO_REPEAT, rule: 'weekly' });
                    setWeekRepeatOpen(true);
                  }}
                >
                  <SyncIcon />
                  Repeat this week
                </Button>
              ) : null}
              <Button data-testid="crm-new-slot" onClick={() => openNewSlot(view === 'day' ? anchor : new Date())}>
                <PlusIcon />
                New slot
              </Button>
            </span>
          )
        }
      />

      {refusal ? <RefusalNotice detail={refusal} className="mt-[-8px]" testId="crm-calendar-refusal" /> : null}
      <CrmMessage message={notice?.text ?? null} tone={notice?.tone ?? 'info'} testId="crm-calendar-notice" />

      <div className="flex flex-wrap items-center justify-between gap-3 border border-hairline bg-surface p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" data-testid="crm-calendar-prev" onClick={() => setAnchor(addDays(anchor, view === 'week' ? -7 : -1))}>
            ‹ Prev
          </Button>
          {/* A real date picker: the browser's own month calendar, which is the
              fastest way to jump three weeks out. It sets the anchor, so both the
              week and the day view follow it. */}
          <label className="flex items-center">
            <input
              type="date"
              data-testid="crm-calendar-jump"
              value={toDateInput(anchor)}
              onChange={(event) => {
                const value = event.target.value;
                if (value === '') return;
                setAnchor(new Date(`${value}T12:00:00`));
              }}
              className="h-8 rounded-chat border border-hairline bg-surface px-2 text-[12px] font-bold text-ink"
            />
          </label>
          <Button size="sm" variant="outline" data-testid="crm-calendar-next" onClick={() => setAnchor(addDays(anchor, view === 'week' ? 7 : 1))}>
            Next ›
          </Button>
          <span className="l2f-tabular font-mono text-[13px] font-bold text-ink" data-testid="crm-calendar-range">
            {rangeLabel}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Which pack's slots. Gym calendars mix open availability with
              several packs, and the useful question is usually "is this pack
              filling up?" rather than "what is on today". */}
          <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
            Pack
            <select
              data-testid="crm-calendar-pack-filter"
              value={packFilter}
              onChange={(event) => setPackFilter(event.target.value)}
              className="h-8 border border-hairline bg-surface px-2 text-[12px] font-bold text-ink"
            >
              <option value="all">All slots ({slots.length})</option>
              <option value="open">
                No pack ({slots.filter((slot) => slot.packId === null).length})
              </option>
              {livePacks.map((pack) => (
                <option key={pack.packId} value={pack.packId}>
                  {pack.name} ({slots.filter((slot) => slot.packId === pack.packId).length})
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Schedule view">
          <Button
            size="sm"
            variant={view === 'week' ? 'secondary' : 'outline'}
            aria-pressed={view === 'week'}
            data-testid="crm-view-week"
            onClick={() => setView('week')}
          >
            Week
          </Button>
          <Button
            size="sm"
            variant={view === 'day' ? 'secondary' : 'outline'}
            aria-pressed={view === 'day'}
            data-testid="crm-view-day"
            onClick={() => setView('day')}
          >
            Day
          </Button>
        </div>
      </div>

      {formOpen ? (
        <Panel
          title="New slot"
          subtitle="Set the window and the places. An individual slot is structurally one place."
          testId="crm-slot-form"
          actions={
            <Button size="sm" variant="ghost" onClick={() => setFormOpen(false)}>
              Close
            </Button>
          }
        >
          <form
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              void create();
            }}
          >
            <Field label="Starts">
              <input
                type="datetime-local"
                data-testid="crm-slot-start"
                value={form.start}
                onChange={(event) => setForm({ ...form, start: event.target.value })}
                className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
              />
            </Field>
            <Field label="Ends">
              <input
                type="datetime-local"
                data-testid="crm-slot-end"
                value={form.end}
                onChange={(event) => setForm({ ...form, end: event.target.value })}
                className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
              />
            </Field>
            <Field label="Kind" info="Individual is a one-to-one (capacity 1). Group is a shared session with N places.">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={form.kind === 'individual' ? 'secondary' : 'outline'}
                  aria-pressed={form.kind === 'individual'}
                  data-testid="crm-slot-kind-individual"
                  onClick={() => setForm({ ...form, kind: 'individual', capacity: '1' })}
                >
                  1-to-1
                </Button>
                <Button
                  size="sm"
                  variant={form.kind === 'group' ? 'secondary' : 'outline'}
                  aria-pressed={form.kind === 'group'}
                  data-testid="crm-slot-kind-group"
                  onClick={() => setForm({ ...form, kind: 'group', capacity: form.capacity === '1' ? '3' : form.capacity })}
                >
                  Group
                </Button>
              </div>
            </Field>
            <Field label="Capacity" info="How many places the slot has. The API refuses a booking once they are full.">
              <input
                type="number"
                min={1}
                max={500}
                data-testid="crm-slot-capacity"
                value={form.capacity}
                disabled={form.kind === 'individual'}
                onChange={(event) => setForm({ ...form, capacity: event.target.value })}
                className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink disabled:opacity-50"
              />
            </Field>
            <Field label="Location">
              <input
                type="text"
                data-testid="crm-slot-location"
                value={form.location}
                placeholder="Studio floor, Zoom…"
                onChange={(event) => setForm({ ...form, location: event.target.value })}
                className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
              />
            </Field>
            <Field
              label="Pack"
              info="Optional. A pack slot is bookable only by a client subscribed to that pack; leave open for any of your clients."
            >
              <select
                data-testid="crm-slot-pack"
                value={form.packId}
                onChange={(event) => setForm({ ...form, packId: event.target.value })}
                className="h-10 w-full min-w-0 border border-hairline bg-page px-2 text-[13px] text-ink"
              >
                <option value="">Open — any client</option>
                {livePacks.map((pack) => (
                  <option key={pack.packId} value={pack.packId}>
                    {pack.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Notes">
              <textarea
                data-testid="crm-slot-notes"
                value={form.notes}
                rows={2}
                onChange={(event) => setForm({ ...form, notes: event.target.value })}
                className="w-full min-w-0 border border-hairline bg-page px-2 py-2 text-[13px] text-ink"
              />
            </Field>
            <div className="sm:col-span-2">
              <RepeatControls draft={repeat} onChange={setRepeat} prefix="crm-slot-repeat" />
            </div>
            <div className="flex flex-wrap gap-2 sm:col-span-2">
              <Button type="submit" data-testid="crm-slot-create" disabled={busy === 'create'}>
                {busy === 'create' ? 'Creating…' : 'Create slot'}
              </Button>
              <Button variant="ghost" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Panel>
      ) : null}

      {weekRepeatOpen ? (
        <Panel
          title="Repeat this week"
          subtitle="Sends this week's one-off slots as one batch plus one rule. The server expands the future occurrences in a single transaction; the slots already on the calendar stay where they are."
          testId="crm-repeat-week-form"
          actions={
            <Button size="sm" variant="ghost" onClick={() => setWeekRepeatOpen(false)}>
              Close
            </Button>
          }
        >
          <div className="flex flex-col gap-3">
            <RepeatControls draft={weekRepeat} onChange={setWeekRepeat} prefix="crm-week-repeat" />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                data-testid="crm-repeat-week-submit"
                disabled={busy === 'repeat-week'}
                onClick={() => void repeatThisWeek()}
              >
                {busy === 'repeat-week'
                  ? 'Repeating…'
                  : `Repeat ${repeatableSlots.length} slot(s) forward`}
              </Button>
              <span className="text-[12px] text-ink-muted">
                One request, {repeatableSlots.length} pattern slot(s).
              </span>
            </div>
          </div>
        </Panel>
      ) : null}

      {slots.length === 0 ? (
        <div data-testid="crm-calendar-empty">
          <EmptyState
            title={view === 'week' ? 'No slots this week' : 'No slots on this day'}
            body="Create availability and clients can be booked into it. Capacity is a rule: a slot stops accepting bookings the moment it is full."
            icon={<TimerIcon className="h-8 w-8 text-accent" />}
            action={
              <Button onClick={() => openNewSlot(view === 'day' ? anchor : new Date())}>
                <PlusIcon />
                Create a slot
              </Button>
            }
          />
        </div>
      ) : visibleSlots.length === 0 ? (
        /* The week has slots, none of them belong to the pack being looked at.
           Saying "no slots this week" here would be a lie about the calendar. */
        <div data-testid="crm-calendar-filtered-empty">
          <EmptyState
            title={
              packFilter === 'open'
                ? 'No open slots this week'
                : `No ${filterName ?? 'pack'} slots this week`
            }
            body="The week has availability, just none for this pack. Clear the filter to see everything, or add a slot for this pack."
            icon={<TimerIcon className="h-8 w-8 text-accent" />}
            action={
              <span className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={() => setPackFilter('all')}>
                  Show every slot
                </Button>
                <Button onClick={() => openNewSlot(view === 'day' ? anchor : new Date())}>
                  <PlusIcon />
                  Create a slot
                </Button>
              </span>
            }
          />
        </div>
      ) : view === 'week' ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
          {days.map((day) => {
            const key = toDateInput(day);
            const list = slotsByDay.get(key) ?? [];
            const isToday = isSameLocalDay(day, new Date());
            return (
              <section
                key={key}
                data-testid={`crm-day-${key}`}
                aria-label={formatDayHeading(day)}
                className={cn(
                  'flex min-w-0 flex-col gap-2 border border-hairline bg-page-subtle p-2',
                  isToday && 'border-accent',
                )}
              >
                <h2 className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                  {formatDayHeading(day)}
                  {isToday ? <span className="text-accent">Today</span> : null}
                </h2>
                {list.length ? (
                  <ul className="flex flex-col gap-1.5">{list.map(renderChip)}</ul>
                ) : (
                  <p className="py-2 text-[12px] text-ink-muted">No slots</p>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <section aria-label={formatDayHeading(anchor)} className="flex flex-col gap-2" data-testid="crm-day-view">
          {daySlots.length ? (
            <ul className="flex flex-col gap-3">{daySlots.map(renderSlot)}</ul>
          ) : (
            <EmptyState
              title={
                packFilter === 'all'
                  ? 'No slots on this day'
                  : packFilter === 'open'
                    ? 'No open slots on this day'
                    : `No ${filterName ?? 'pack'} slots on this day`
              }
              body={
                packFilter === 'all'
                  ? 'Move to another day, or create availability for this one.'
                  : 'This day has no slots for the selected pack. Show every slot, or create one for this pack.'
              }
              icon={<TimerIcon className="h-8 w-8 text-accent" />}
            />
          )}
        </section>
      )}

      <p className="flex items-center gap-2 text-[12px] text-ink-muted">
        <TimerIcon className="text-[14px]" />
        Booking and availability only — no venue management. Refusals come from the API and are shown word for word.
        <ChevronDownIcon className="hidden" aria-hidden="true" />
        <ArrowRightIcon className="hidden" aria-hidden="true" />
      </p>
    </div>
  );
}
